/**
 * src/api/printing/PrintingApi.ts — Façade publique du module impression.
 *
 * Wraps PrintService legacy (2074 LOC) via Strangler Fig — invariant 4.B.5.
 * PrintService legacy n'est PAS réécrit. Ce fichier orchestre uniquement.
 *
 * Expose quatre opérations :
 *   - printTicket(saleId, options)       : ticket fiscal NF525
 *   - printReceipt(payload, deviceId)    : impression libre non fiscale
 *   - printZ(zClosureId)                 : rapport Z (mentions NF525 — invariant 4.A.16)
 *   - listDevices()                      : discovery des imprimantes disponibles
 *
 * Décision migration supabase.* interne PrintService (documentée en commit) :
 *   Les 7 appels supabase dans PrintService.ts sont dispersés dans des fonctions
 *   fortement couplées (Promise.all, formatage inline, 2074 LOC).
 *   Décision : wrapping pur. PrintService conserve ses appels supabase internes.
 *   Marqués DEBT(Phase-D) dans PrintService.ts.
 *   Seul printLog.ts est migré (trivial, 1 appel isolé).
 *   La frontière src/api/printing/ est respectée pour tous les nouveaux flux.
 *
 * Invariants :
 *   4.A.10 — tenant_id jamais dans les params publics (délégué à RLS via PrintService)
 *   4.A.15 — aucun PII dans les logs ni dans PrintResult
 *   4.A.16 — mentions NF525 ticket déléguées à PrintService legacy (wrapper ne les casse pas)
 *   4.B.2  — aucun import supabase direct dans ce fichier (tout passe par logging.ts ou legacy)
 *   4.B.4  — 0 any
 *   4.B.5  — Strangler Fig : pas de réécriture PrintService
 */

import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import {
  printSaleReceipt,
  printZReport,
  listTenantPrinters as listTenantPrintersDb,
} from '@/services/print/PrintService';
import type { PrinterConfig } from '@/services/print/PrintService';
import { invalidInputError, noPrinterConnectedError, formatError } from './errors';
import type { PrintError } from './errors';
import type {
  PrintOptions,
  ReceiptPayload,
  PrintResult,
  PrinterDevice,
  PrinterType,
  KitchenTicketPayload,
} from './types';
import { receiptPayloadSchema, kitchenTicketPayloadSchema } from './types';
import { PrinterAdapter } from './adapters/PrinterAdapter';
import { logPrintAttempt } from './logging';
import { printGroupsDirect, printGroupsWithPersistence } from './kitchen-print-orchestrator';
import type { KitchenSplitGroup } from './kitchen-routing';

/**
 * Convertit une PrinterConfig (row DB legacy) en PrinterDevice (modèle façade).
 * Mapping snake_case → camelCase, valeurs par défaut sûres pour les colonnes nullable.
 */
function mapPrinterConfigToDevice(row: PrinterConfig): PrinterDevice {
  return {
    id: row.id,
    name: row.name,
    connectionType: row.connection_type,
    printerType: row.printer_type,
    btMacAddress: row.bt_mac_address,
    ipAddress: row.ip_address,
    port: row.port ?? 9100,
    registerId: row.register_id,
    isDefault: row.is_default ?? false,
    paperWidth: row.paper_width ?? 80,
    charPerLine: row.char_per_line ?? 48,
  };
}

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------

/**
 * Convertit la route string legacy (printZReport) vers PrintResult['route'].
 * Les valeurs inconnues tombent sur 'fallback'.
 */
function mapLegacyRoute(legacyRoute: string): PrintResult['route'] {
  switch (legacyRoute) {
    case 'aidl':
      return 'sunmi_aidl';
    case 'cloud_printer':
      return 'sunmi_cloud';
    case 'bluetooth':
      return 'bluetooth';
    case 'network':
      return 'network';
    default:
      return 'fallback';
  }
}

/**
 * Construit un PrintResult succès standardisé depuis une route connue.
 */
function buildResult(
  route: PrintResult['route'],
  deviceName: string | null,
  durationMs: number,
  isDuplicate: boolean,
): PrintResult {
  return {
    route,
    deviceName,
    durationMs,
    logId: null, // logId non retourné par log_print_attempt (fire-and-forget)
    isDuplicate,
  };
}

// ---------------------------------------------------------------------------
// Façade PrintingApi
// ---------------------------------------------------------------------------

export const PrintingApi = {
  /**
   * Imprime le ticket fiscal d'une vente par son UUID Supabase.
   *
   * Délègue entièrement à PrintService.printSaleReceipt() qui :
   *   - charge la vente + items + tenant depuis Supabase (internals legacy)
   *   - formate le ticket selon ticket_config (invariant 4.A.16 respecté)
   *   - dispatch vers le meilleur printer disponible
   *
   * Invariant 4.A.16 : les mentions NF525 sont gérées par PrintService legacy.
   * Ce wrapper NE surcharge PAS le formatage — il orchestre uniquement.
   *
   * @param saleId   UUID Postgres de la vente (sales.id)
   * @param tenantId UUID du tenant courant (requis par PrintService legacy)
   * @param options  Options d'impression (registerId, isDuplicate, deviceId)
   */
  async printTicket(
    saleId: string,
    tenantId: string,
    options?: PrintOptions,
  ): Promise<Result<PrintResult, PrintError>> {
    if (!saleId || typeof saleId !== 'string') {
      return err(invalidInputError('saleId', 'saleId est requis'));
    }
    if (!tenantId || typeof tenantId !== 'string') {
      return err(invalidInputError('tenantId', 'tenantId est requis'));
    }

    const opts = options ?? { isDuplicate: false };
    const startAt = Date.now();

    try {
      const success = await printSaleReceipt(tenantId, saleId, {
        registerId: opts.registerId,
        isDuplicate: opts.isDuplicate,
      });

      const durationMs = Date.now() - startAt;

      // Log fire-and-forget (invariant 4.A.15 : pas de PII dans metadata)
      void logPrintAttempt({
        documentType: 'receipt',
        route: 'unknown', // la route effective est déterminée par PrintService legacy
        success,
        registerId: opts.registerId ?? null,
        durationMs,
      });

      if (!success) {
        return err(noPrinterConnectedError());
      }

      return ok(buildResult('fallback', null, durationMs, opts.isDuplicate ?? false));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // Vente non trouvée → le legacy throw "Vente non trouvée"
      if (message.includes('Vente non trouvée') || message.includes('not found')) {
        return err({ type: 'sale_not_found', saleId });
      }
      return err(formatError(message));
    }
  },

  /**
   * Impression libre (non fiscale) à partir d'un payload structuré.
   *
   * Pas de fetch Supabase ici — le payload contient toutes les données.
   * Délègue le dispatch hardware à PrinterAdapter.
   *
   * @param payload  Données du document à imprimer
   * @param deviceId Identifiant optionnel du périphérique cible
   */
  async printReceipt(
    payload: ReceiptPayload,
    deviceId?: string,
  ): Promise<Result<PrintResult, PrintError>> {
    const parsed = receiptPayloadSchema.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const field = first?.path.join('.') ?? 'payload';
      const message = first?.message ?? 'Données invalides';
      return err(invalidInputError(field, message));
    }

    const startAt = Date.now();

    // Délègue à PrinterAdapter qui orchestre le dispatch hardware
    const result = await PrinterAdapter.dispatchPrint('receipt', async () => {
      // Impression texte libre via TextEncoder + ESC/POS minimal
      // Pour le mode receipt libre, on formate les lignes en texte brut
      // Le formatage avancé ESC/POS reste dans PrintService legacy
      const lines = parsed.data.lines;
      if (lines.length === 0) return false;
      // best-effort : si le bridge Sunmi est dispo, on tente l'impression directe
      if (typeof window !== 'undefined') {
        const w = window as typeof window & Record<string, unknown>;
        const bridge = (w['SunmiPrinter'] ?? w['SunmiInnerPrinter']) as
          | Record<string, unknown>
          | undefined;
        if (bridge && typeof bridge['printText'] === 'function') {
          try {
            (bridge['printText'] as (t: string) => void)(lines.join('\n'));
            if (typeof bridge['feedLines'] === 'function') {
              (bridge['feedLines'] as (n: number) => void)(3);
            }
            return true;
          } catch {
            return false;
          }
        }
      }
      return false;
    });

    if (!result.ok) return result;

    const durationMs = Date.now() - startAt;
    void logPrintAttempt({
      documentType: 'other',
      route: 'unknown',
      success: true,
      durationMs,
    });

    return ok(
      buildResult(
        result.value.route,
        result.value.deviceName,
        durationMs,
        parsed.data.options?.isDuplicate ?? false,
      ),
    );
  },

  /**
   * Impression du rapport Z (clôture journalière NF525).
   *
   * Invariant 4.A.16 : les mentions NF525 obligatoires (totaux, séquences,
   * hash chaîné) sont générées par PrintService.printZReport legacy.
   * Ce wrapper NE génère PAS le contenu fiscal — il orchestre uniquement.
   *
   * Décision de design : printZReport legacy requiert `closureData` déjà chargé
   * (l'appelant a déjà la z_closure via useZClosure ou la page de clôture).
   * PrintingApi reçoit ces données et les délègue sans appel Supabase supplémentaire.
   *
   * @param tenantId    UUID du tenant courant
   * @param registerId  Identifiant de caisse (optionnel)
   * @param closureData Données de la clôture Z (chargées par l'appelant)
   * @param options     Options d'impression
   */
  async printZ(
    tenantId: string,
    registerId: string | undefined,
    closureData: Record<string, unknown>,
    options?: PrintOptions,
  ): Promise<Result<PrintResult, PrintError>> {
    if (!tenantId || typeof tenantId !== 'string') {
      return err(invalidInputError('tenantId', 'tenantId est requis'));
    }
    if (!closureData || typeof closureData !== 'object') {
      return err(invalidInputError('closureData', 'closureData est requis'));
    }

    const startAt = Date.now();

    try {
      const legacy = await printZReport(tenantId, registerId, closureData);
      const durationMs = Date.now() - startAt;

      void logPrintAttempt({
        documentType: 'z_report',
        route: (legacy.route as import('./logging').PrintLogRoute) ?? 'unknown',
        success: legacy.ok,
        registerId: registerId ?? null,
        printerName: legacy.printerName ?? null,
        printerId: legacy.printerId ?? null,
        printerMac: legacy.printerMac ?? null,
        errorMessage: legacy.error ?? null,
        durationMs,
      });

      if (!legacy.ok) {
        if (legacy.error?.includes('Aucune imprimante')) {
          return err(noPrinterConnectedError());
        }
        return err(formatError(legacy.error ?? 'Échec impression Z'));
      }

      const route = mapLegacyRoute(legacy.route);
      return ok(buildResult(route, legacy.printerName ?? null, durationMs, false));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return err(formatError(message));
    }
  },

  /**
   * Imprime un bon de commande cuisine (KOT) ou bar.
   *
   * Pas d'impact fiscal (KOT ≠ ticket NF525). Aucune table fiscale touchée.
   *
   * Résolution imprimante :
   *   - Si printerId fourni → match exact (cible du picker)
   *   - Sinon → résolution auto par printer_type='kitchen' + registerId (legacy)
   *
   * Modes d'idempotence :
   *   - Sans idempotencyKey : cache mémoire process (#234) — comportement inchangé
   *     (picker sans clé = réimpression volontaire toujours possible).
   *   - Avec idempotencyKey : persistence DB via kot_print_jobs (T2 + lot 4).
   *     Compose avec printerId : la cible physique vient du picker, l'idempotence
   *     reste persistante (précondition : clé UNIQUE PAR ENVOI — suffixe horodaté).
   *     Les groupes 'printed' (même par un autre device) sont sautés automatiquement ;
   *     un retry avec la même clé ne ré-imprime que les groupes failed/pending.
   *     Dégradé réseau → fallback sur le cache mémoire (jamais de blocage d'impression).
   *
   * @param tenantId        UUID du tenant courant
   * @param payload         Données du KOT (destination, items, etc.)
   * @param registerId      Identifiant de caisse (optionnel, résolution auto)
   * @param printerId       Identifiant imprimante explicite (override picker)
   * @param idempotencyKey  Clé d'idempotence persistante — orderNumber (caisse) ou
   *                        draftId:sent_seq (tablette). Absent = comportement #234.
   */
  async printKitchenTicket(
    tenantId: string,
    payload: KitchenTicketPayload,
    registerId?: string,
    printerId?: string,
    idempotencyKey?: string,
  ): Promise<Result<PrintResult, PrintError>> {
    if (!tenantId || typeof tenantId !== 'string') {
      return err(invalidInputError('tenantId', 'tenantId est requis'));
    }

    const parsed = kitchenTicketPayloadSchema.safeParse(payload);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      const field = first?.path.join('.') ?? 'payload';
      const message = first?.message ?? 'Données invalides';
      return err(invalidInputError(field, message));
    }

    const startAt = Date.now();

    // Résolution des groupes — identique dans les deux modes.
    // Import dynamique : le resolver (et son client supabase) n'est chargé que
    // si le split est réellement possible. Fallback groupe unique si erreur.
    let groups: KitchenSplitGroup[] = [{ printerFunction: null, items: parsed.data.items }];
    if (!printerId && parsed.data.items.some((i) => i.productId)) {
      try {
        const { resolveKitchenSplit } = await import('./kitchen-routing');
        groups = await resolveKitchenSplit(tenantId, parsed.data.items);
      } catch {
        groups = [{ printerFunction: null, items: parsed.data.items }];
      }
    }

    try {
      // Dispatch selon la présence d'une clé d'idempotence persistante.
      // Lot 4 : la persistance compose avec le picker (printerId = cible physique).
      // Sans clé, le picker garde le comportement #234 (réimpression volontaire OK).
      const { allOk } = idempotencyKey
        ? await printGroupsWithPersistence(
            tenantId,
            groups,
            parsed.data,
            idempotencyKey,
            registerId,
            printerId,
          )
        : await printGroupsDirect(tenantId, groups, parsed.data, registerId, printerId);

      if (!allOk) {
        return err(noPrinterConnectedError());
      }
      return ok(buildResult('fallback', null, Date.now() - startAt, false));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (message.includes('Aucune imprimante')) {
        return err(noPrinterConnectedError());
      }
      return err(formatError(message));
    }
  },

  /**
   * Liste les périphériques d'impression disponibles (découverte BT + réseau).
   * Délègue à PrinterAdapter.listDevices().
   */
  async listDevices(timeoutMs = 8000): Promise<Result<PrinterDevice[], PrintError>> {
    return PrinterAdapter.listDevices(timeoutMs);
  },

  /**
   * Liste les imprimantes configurées d'un tenant, filtrées par types.
   *
   * Utilisé par le picker manuel (KOT) pour proposer les cibles cuisine/bar.
   * Différent de listDevices() (qui découvre le hardware BT+réseau) :
   * ici on lit la config tenant_printers persistée.
   *
   * @param tenantId UUID du tenant courant (requis pour RLS, invariant 4.A.10)
   * @param types    Filtrage par printer_type (ex: ['kitchen','bar']). Vide = tous.
   */
  async listTenantPrinters(
    tenantId: string,
    types?: PrinterType[],
  ): Promise<Result<PrinterDevice[], PrintError>> {
    if (!tenantId || typeof tenantId !== 'string') {
      return err(invalidInputError('tenantId', 'tenantId est requis'));
    }

    try {
      const rows = await listTenantPrintersDb(tenantId, types);
      return ok(rows.map(mapPrinterConfigToDevice));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      return err(formatError(message));
    }
  },
};
